import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/misc/PageHeader';
import PageWithTabsLayout from '../../components/layouts/PageWithTabsLayout';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import EventReportsTab from './EventReportsTab';
import EventsOverviewTab from './EventsOverviewTab';
import BirthdaysTab from './BirthdaysTab';
import EventsAgendaTab from './EventsAgendaTab';
import ExternalCalendarsTab from './ExternalCalendarsTab';

export default function GeneralEventPage(): JSX.Element {
  const { t } = useTranslation();

  const EventPageTab = {
    CALENDAR: t('page.event.tabs.calendar'),
    EVENTS: t('page.event.tabs.events'),
    ATTENDANCE: t('page.event.tabs.attendance'),
    BIRTHDAYS: t('page.event.tabs.birthdays'),
    EXTERNAL_CALENDARS: t('page.event.tabs.externalCalendars'),
  } as const;

  type EventPage = typeof EventPageTab[keyof typeof EventPageTab];

  const navigate = useNavigate();
  const location = useLocation();
  
  const getInitialTab = (): EventPage => {
    const stateTab = location.state?.tab;
    switch (stateTab) {
      case 'calendar':
        return EventPageTab.CALENDAR;
      case 'external-calendars':
        return EventPageTab.EXTERNAL_CALENDARS;
      case 'attendance':
        return EventPageTab.ATTENDANCE;
      case 'birthdays':
        return EventPageTab.BIRTHDAYS;
      default:
        return EventPageTab.EVENTS;
    }
  };

  const [activeTab, setActiveTab] = React.useState<EventPage>(getInitialTab());

  // Update active tab when location state changes
  React.useEffect(() => {
    setActiveTab(getInitialTab());
  }, [location.state?.tab]);

  const renderTabContent = (tab: EventPage): JSX.Element => {
    switch (tab) {
      case EventPageTab.CALENDAR:
        return <EventsAgendaTab />;
      case EventPageTab.EVENTS:
        return <EventsOverviewTab />;
      case EventPageTab.ATTENDANCE:
        return <EventAttendanceTab />;
      case EventPageTab.BIRTHDAYS:
        return <BirthdaysTab />;
      case EventPageTab.EXTERNAL_CALENDARS:
        return <ExternalCalendarsTab />;
      default:
        return <></>;
    }
  };

  return (
    <div>
      <PageHeader
        title={t('page.event.regular.title')}
        subtitle={t('page.event.regular.subtitle')}
        primaryButton={
          <PrimaryButton
            text={t('page.event.regular.add')}
            icon={FaPlus}
            onClick={() => navigate('/events/create')}
          />
        }
      />

      <PageWithTabsLayout
        tabs={Object.values(EventPageTab)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        renderTabContent={renderTabContent}
      />
    </div>
  );
}

function EventAttendanceTab(): JSX.Element {
  return (
    <div className='w-full h-full'>
      <EventReportsTab />
    </div>
  );
}